import { StatsShimmer, TableShimmer, CardShimmer, HeroShimmer, ShimmerBlock, ListShimmer } from './Shimmer';
export * from './Shimmer';

// Custom Hero Shimmer for Job Description to match real height/style
const JobHeroShimmer = () => (
    <div style={{
        background: 'linear-gradient(135deg, rgba(29, 78, 216, 0.1), rgba(37, 99, 235, 0.1))',
        padding: '2rem 0 1.5rem',
        marginBottom: '1.2rem'
    }}>
        <div className="container">
            <div className="d-flex align-items-center flex-wrap gap-4">
                <ShimmerBlock
                    width="100px"
                    height="100px"
                    className="rounded"
                    style={{ border: '3px solid white', backgroundColor: 'white' }}
                />
                <div className="flex-grow-1" style={{ minWidth: '200px' }}>
                    <ShimmerBlock width="40%" height="32px" className="mb-2" />
                    <ShimmerBlock width="20%" height="16px" className="mb-3 opacity-75" />
                    <div className="d-flex gap-3 flex-wrap">
                        <ShimmerBlock width="80px" height="24px" className="rounded-pill" />
                        <ShimmerBlock width="80px" height="24px" className="rounded-pill" />
                        <ShimmerBlock width="80px" height="24px" className="rounded-pill" />
                    </div>
                </div>
            </div>
        </div>
    </div>
);

/**
 * Job Description Page Shimmer
 * Structure: Hero + 2 Column Layout (Main Content + Sidebar)
 */
export const JobShimmer = () => (
    <div className="page-shimmer job-shimmer">
        <JobHeroShimmer />
        <div className="container">
            <div className="row">
                <div className="col-lg-8">
                    <div className="card mb-4 p-4">
                        <ShimmerBlock width="40%" height="24px" className="mb-3" />
                        <div className="d-flex gap-4 mb-4">
                            <ShimmerBlock width="100px" height="60px" />
                            <ShimmerBlock width="100px" height="60px" />
                            <ShimmerBlock width="100px" height="60px" />
                        </div>
                        <ShimmerBlock width="100%" height="16px" className="mb-2" />
                        <ShimmerBlock width="100%" height="16px" className="mb-2" />
                        <ShimmerBlock width="100%" height="16px" className="mb-2" />
                        <ShimmerBlock width="80%" height="16px" className="mb-4" />

                        <ShimmerBlock width="30%" height="20px" className="mb-3 mt-4" />
                        <ShimmerBlock width="100%" height="16px" className="mb-2" />
                        <ShimmerBlock width="90%" height="16px" className="mb-2" />
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="card p-3 mb-3">
                        <ShimmerBlock width="50%" height="20px" className="mb-3" />
                        {[1, 2, 3, 4, 5].map(i => (
                            <div key={i} className="d-flex align-items-center mb-3">
                                <ShimmerBlock width="24px" height="24px" className="me-2 rounded" />
                                <div className="flex-grow-1">
                                    <ShimmerBlock width="40%" height="12px" className="mb-1" />
                                    <ShimmerBlock width="70%" height="14px" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </div>
);

/**
 * Profile Page Shimmer
 * Structure: Hero + Stats Grid + Sidebar/Main Layout
 */
export const ProfileShimmer = () => (
    <div className="page-shimmer profile-shimmer">
        {/* Full width Hero with gradient to match real profile */}
        <div style={{
            background: 'linear-gradient(135deg, rgba(29, 78, 216, 0.1), rgba(37, 99, 235, 0.1))',
            padding: '2rem 0 9rem',
            marginBottom: '-5rem',
            width: '100%',
            height: '24rem',
        }}>
            <div className="container">
                <div className="d-flex align-items-center flex-wrap gap-4">
                    <div className="position-relative">
                        <ShimmerBlock
                            width="100px"
                            height="100px"
                            className="rounded-circle"
                            style={{ border: '3px solid white' }}
                        />
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: '200px' }}>
                        <ShimmerBlock width="40%" height="32px" className="mb-2" />
                        <ShimmerBlock width="60%" height="18px" className="mb-3 opacity-75" />
                        <div className="d-flex gap-3">
                            <ShimmerBlock width="100px" height="16px" />
                            <ShimmerBlock width="120px" height="36px" className="rounded" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div className="container">
            {/* Stats Grid - using grid syntax to match real page */}
            <div
                className="profile-shimmer-stats"
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    marginBottom: '2rem',
                    position: 'relative',
                    zIndex: 2
                }}>
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="card p-3 d-flex flex-row align-items-center gap-3">
                        <ShimmerBlock width="40px" height="40px" className="rounded-circle flex-shrink-0" />
                        <div className="flex-grow-1">
                            <ShimmerBlock width="40%" height="24px" className="mb-1" />
                            <ShimmerBlock width="60%" height="14px" />
                        </div>
                    </div>
                ))}
            </div>

            <div className="row">
                {/* Sidebar - 3 cols */}
                <div className="col-lg-3">
                    {/* Completion */}
                    <div className="card p-3 mb-3 text-center">
                        <ShimmerBlock width="70%" height="20px" className="mx-auto mb-4" />
                        <div className="d-flex justify-content-center mb-3">
                            <ShimmerBlock width="100px" height="100px" className="rounded-circle" />
                        </div>
                        <ShimmerBlock width="90%" height="14px" className="mx-auto" />
                    </div>

                    {/* Quick Actions - Inlined to fix overflow */}
                    <div className="card p-3 mb-3">
                        <ShimmerBlock width="60%" height="20px" className="mb-3" />
                        {[1, 2, 3].map(i => (
                            <div key={i} className="d-flex align-items-center mb-3">
                                <ShimmerBlock width="32px" height="32px" className="rounded-circle me-2 flex-shrink-0" />
                                <div className="flex-grow-1">
                                    <ShimmerBlock width="70%" height="14px" />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Support */}
                    <div className="card p-3 mb-3">
                        <ShimmerBlock width="50%" height="20px" className="mb-3" />
                        {[1, 2].map(i => (
                            <div key={i} className="d-flex align-items-center mb-3">
                                <ShimmerBlock width="26px" height="26px" className="rounded-circle me-2 flex-shrink-0" />
                                <div className="flex-grow-1">
                                    <ShimmerBlock width="50%" height="14px" className="mb-1" />
                                    <ShimmerBlock width="80%" height="12px" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Main Content - 9 cols */}
                <div className="col-lg-9">
                    {/* About */}
                    <div className="card p-4 mb-3">
                        <div className="d-flex justify-content-between mb-4">
                            <ShimmerBlock width="100px" height="24px" />
                            <ShimmerBlock width="60px" height="32px" />
                        </div>
                        <ShimmerBlock width="100%" height="16px" className="mb-2" />
                        <ShimmerBlock width="100%" height="16px" className="mb-2" />
                        <ShimmerBlock width="90%" height="16px" />
                    </div>

                    {/* Cover Letter */}
                    <div className="card p-4 mb-3">
                        <div className="d-flex justify-content-between mb-4">
                            <ShimmerBlock width="140px" height="24px" />
                            <ShimmerBlock width="60px" height="32px" />
                        </div>
                        <ShimmerBlock width="100%" height="16px" className="mb-2" />
                        <ShimmerBlock width="80%" height="16px" />
                    </div>

                    {/* Experience */}
                    <div className="card p-4 mb-3">
                        <div className="d-flex justify-content-between mb-4">
                            <ShimmerBlock width="120px" height="24px" />
                            <ShimmerBlock width="80px" height="32px" />
                        </div>
                        <ListShimmer items={2} />
                    </div>

                    {/* Education */}
                    <div className="card p-4">
                        <div className="d-flex justify-content-between mb-4">
                            <ShimmerBlock width="120px" height="24px" />
                        </div>
                        <ListShimmer items={2} />
                    </div>
                </div>
            </div>
        </div>
    </div>
);

/**
 * Community Page Shimmer
 * Structure: Hero + Feed Layout
 */
export const CommunityShimmer = () => (
    <div className="page-shimmer community-shimmer">
        <div className="container mt-4">
            <div className="row">
                <div className="col-12">
                    <div className="mb-4">
                        <ShimmerBlock width="100%" height="50px" className="rounded" />
                    </div>
                    <CardShimmer count={3} columns={1} />
                </div>
            </div>
        </div>
    </div>
);

/**
 * About Us Page Shimmer
 * Structure: Hero + Masonry/Grid Stats + Team
 */
export const AboutShimmer = () => (
    <div className="page-shimmer about-shimmer">
        <HeroShimmer />
        <div className="container mt-5">
            <div className="text-center mb-5">
                <ShimmerBlock width="30%" height="32px" className="mx-auto mb-3" />
                <ShimmerBlock width="60%" height="16px" className="mx-auto" />
            </div>
            <div className="row mb-5">
                {[1, 2, 3].map(i => (
                    <div key={i} className="col-md-4">
                        <ShimmerBlock width="100%" height="200px" className="rounded mb-3" />
                    </div>
                ))}
            </div>
            <StatsShimmer count={4} />
        </div>
    </div>
);

/**
 * Companies Page Shimmer
 * Structure: Hero + Filter Bar + Grid
 */
export const CompaniesShimmer = () => (
    <div className="page-shimmer companies-shimmer">
        <HeroShimmer />
        <div className="container mt-4">
            <div className="d-flex gap-3 mb-4">
                <ShimmerBlock width="100%" height="50px" className="rounded" />
                <ShimmerBlock width="150px" height="50px" className="rounded" />
            </div>
            <div className="row">
                <div className="col-md-4 mb-4"><ShimmerBlock width="100%" height="250px" className="rounded" /></div>
                <div className="col-md-4 mb-4"><ShimmerBlock width="100%" height="250px" className="rounded" /></div>
                <div className="col-md-4 mb-4"><ShimmerBlock width="100%" height="250px" className="rounded" /></div>
                <div className="col-md-4 mb-4"><ShimmerBlock width="100%" height="250px" className="rounded" /></div>
                <div className="col-md-4 mb-4"><ShimmerBlock width="100%" height="250px" className="rounded" /></div>
                <div className="col-md-4 mb-4"><ShimmerBlock width="100%" height="250px" className="rounded" /></div>
            </div>
        </div>
    </div>
);

/**
 * Company Profile/Partner Page Shimmer
 * Structure: Hero + Sidebar info + Content (Jobs/Notices)
 */
export const CompanyProfileShimmer = () => (
    <div className="page-shimmer company-profile-shimmer">
        <HeroShimmer />
        <div className="container mt-4">
            <StatsShimmer count={4} />
            <div className="row mt-4">
                <div className="col-lg-8">
                    <div className="mb-4">
                        <div className="d-flex justify-content-between mb-3">
                            <ShimmerBlock width="30%" height="28px" />
                            <ShimmerBlock width="100px" height="20px" />
                        </div>
                        <div className="row">
                            <div className="col-md-6 mb-3"><ShimmerBlock width="100%" height="150px" className="rounded" /></div>
                            <div className="col-md-6 mb-3"><ShimmerBlock width="100%" height="150px" className="rounded" /></div>
                        </div>
                    </div>
                    <div>
                        <ShimmerBlock width="40%" height="28px" className="mb-3" />
                        <ListShimmer items={3} />
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="card p-3 mb-3">
                        <ShimmerBlock width="50%" height="24px" className="mb-3" />
                        <ListShimmer items={4} />
                    </div>
                </div>
            </div>
        </div>
    </div>
);

/**
 * Custom Page Shimmer
 * Structure: Simple Hero + Text Block for generic custom pages
 */
export const CustomPageShimmer = () => (
    <div className="page-shimmer custom-page-shimmer">
        <HeroShimmer />
        <div className="container mt-5">
            <div className="row justify-content-center">
                <div className="col-lg-10">
                    <ShimmerBlock width="40%" height="40px" className="mb-4 mx-auto" />
                    <ShimmerBlock width="100%" height="16px" className="mb-2" />
                    <ShimmerBlock width="100%" height="16px" className="mb-2" />
                    <ShimmerBlock width="90%" height="16px" className="mb-4" />

                    <div className="row mt-5">
                        <div className="col-md-6 mb-4">
                            <ShimmerBlock width="100%" height="200px" className="rounded" />
                        </div>
                        <div className="col-md-6 mb-4">
                            <ShimmerBlock width="100%" height="20px" className="mb-3" />
                            <ListShimmer items={4} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

/**
 * PageShimmer - Generic shimmer entry point
 */
export const PageShimmer = ({ variant = 'table', ...props }) => {
    switch (variant) {
        case 'dashboard':
            return (
                <div className="page-shimmer">
                    <HeroShimmer />
                    <StatsShimmer count={props.statsCount || 4} />
                    <div className="mt-4">
                        <TableShimmer rows={5} columns={5} />
                    </div>
                </div>
            );
        case 'cards':
            return (
                <div className="page-shimmer">
                    <HeroShimmer />
                    <CardShimmer count={props.cardCount || 6} columns={props.cardColumns || 3} variant={props.cardVariant || 'default'} />
                </div>
            );
        case 'job':
            return <JobShimmer />;
        case 'profile':
            return <ProfileShimmer />;
        case 'community':
            return <CommunityShimmer />;
        case 'about':
            return <AboutShimmer />;
        case 'companies':
            return <CompaniesShimmer />;
        case 'company-profile':
            return <CompanyProfileShimmer />;
        case 'custom-page':
            return <CustomPageShimmer />;
        default:
            return (
                <div className="page-shimmer">
                    <HeroShimmer />
                    <StatsShimmer count={props.statsCount || 4} />
                    <div className="mt-4">
                        <TableShimmer rows={props.rows || 10} columns={5} />
                    </div>
                </div>
            );
    }
};

export default PageShimmer;
